import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { graphql, useFragment } from 'react-relay';
import { TopLevelSpacer } from '@atlaskit/navigation-system';
import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';
import { NavItemsContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { getConfigurableMenuIds } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-configurable-menu-ids/index.tsx';
import {
	getAppItemsData,
	getProductItemsData,
	getVisibleAppItemsData,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/items-data/index.tsx';
import type { ConfigurableMenuItems$key } from '@atlassian/jira-relay/src/__generated__/ConfigurableMenuItems.graphql';
import { GlobalAppShortcuts } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts/src/index.tsx';
import { useGlobalApps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isTeamsAppEnabled } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { ConfigurableMenuItem } from '../../common/ui/configurable-menu-item/ConfigurableMenuItem.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '../../controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import { useRefreshL1MenuItems } from '../../controllers/use-refresh-l1-menu-items/index.tsx';
import { MoreMenuIsOpenContextProvider } from './more-menu-is-open-context-provider/index.tsx';
import { SidebarCustomization } from './sidebar-customization/SidebarCustomization.tsx';
import { PeekMenuIdContextProvider } from './peek-menu-id-context-provider/index.tsx';
import {
	GlobalShortcuts,
	GlobalShortcutsWithInvitePeopleProvider,
} from './global-shortcuts/index.tsx';

export type ConfigurableMenuItemsProps = {
	menuData: ConfigurableMenuItems$key;
};

export const ConfigurableMenuItems = memo(ConfigurableMenuItemsInner);

function ConfigurableMenuItemsInner({ menuData }: ConfigurableMenuItemsProps) {
	const data = useFragment<ConfigurableMenuItems$key>(
		graphql`
			fragment ConfigurableMenuItems on Query {
				jira @optIn(to: "JiraUserNavConfig") {
					userNavigationConfiguration(cloudID: $cloudId, navKey: "NAV_SIDEBAR") {
						navItems {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							menuId @required(action: THROW)
							# eslint-disable-next-line @atlassian/relay/unused-fields
							isVisible @required(action: THROW)
						}
					}
				}
				...ConfigurableMenuItem
			}
		`,
		menuData,
	);

	// Creates a synced ref to the global app shortcuts to get the response from global app shortcuts component.
	// const getFilteredProductLinks = useRef<fnGetFilteredProductLinks | undefined>(undefined);
	const [getFilteredProductLinks, setGetFilteredProductLinks] = useState<
		fnGetFilteredProductLinks | undefined
	>(undefined);

	const globalAppLicense = useGlobalApps();

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		// Add back any missing menu ids before filtering in case licensing has changed or a new L1 has been created.
		// This also handles when a user has no configuration yet.
		useRefreshL1MenuItems(data.jira?.userNavigationConfiguration?.navItems),
	);

	const navItemsContext = useMemo(
		() => ({
			navItems,
			visibleMenuIds: getConfigurableMenuIds(navItems, true),
			moreMenuIds: getConfigurableMenuIds(navItems, false),
			productItemsData: getProductItemsData(navItems),
		}),
		[navItems],
	);

	const visibleGlobalAppMenuIds = useMemo(
		() => getVisibleAppItemsData(navItems, globalAppLicense, getFilteredProductLinks),
		[globalAppLicense, navItems, getFilteredProductLinks],
	);
	const appItemsDataUnstable = useMemo(
		() => getAppItemsData(navItems, globalAppLicense, getFilteredProductLinks),
		[globalAppLicense, navItems, getFilteredProductLinks],
	);

	/**
	 * Unfortunately appItemsDataUnstable is not stable, as setGetFilteredProductLinks gets called later to update getFilteredProductLinks.
	 * However, even when getFilteredProductLinks changes, appItemsData does not always change meaningfully (eg same data but new instantiation).
	 * This means that we can use a stable ref to avoid unnecessary re-renders of the GlobalShortcuts component.
	 */
	const [appItemsDataStable, setAppItemsDataStable] = useState(appItemsDataUnstable);
	useEffect(() => {
		if (!isEqual(appItemsDataStable, appItemsDataUnstable)) {
			setAppItemsDataStable(appItemsDataUnstable);
		}
	}, [appItemsDataStable, appItemsDataUnstable]);

	const handleUpdateGetFilteredProductLinks = useCallback((fn: fnGetFilteredProductLinks) => {
		setGetFilteredProductLinks(() => fn);
	}, []);
	// Teams App requires the invite modal context provider to be wrapped around it.
	const globalShortcuts = useMemo(() => {
		if (fg('nav4_global_links_jira')) {
			return (
				<>
					<TopLevelSpacer />
					{isTeamsAppEnabled() ? (
						<GlobalShortcutsWithInvitePeopleProvider
							appItemsData={appItemsDataStable}
							handleUpdateGetFilteredProductLinks={handleUpdateGetFilteredProductLinks}
						/>
					) : (
						<GlobalShortcuts
							appItemsData={appItemsDataStable}
							handleUpdateGetFilteredProductLinks={handleUpdateGetFilteredProductLinks}
						/>
					)}
				</>
			);
		}

		return visibleGlobalAppMenuIds.length > 0 ? (
			<>
				<TopLevelSpacer />
				{visibleGlobalAppMenuIds.map((menuId) => (
					<GlobalAppShortcuts menuId={menuId} key={menuId} />
				))}
			</>
		) : null;
	}, [visibleGlobalAppMenuIds, appItemsDataStable, handleUpdateGetFilteredProductLinks]);

	const getAppShortcutURL = useCallback(
		(apsKey: string) => {
			if (getFilteredProductLinks) {
				const result = getFilteredProductLinks(apsKey);
				return result.promotedLink?.url;
			}
		},
		[getFilteredProductLinks],
	);

	return (
		<NavItemsContext.Provider value={navItemsContext}>
			<PeekMenuIdContextProvider>
				<MoreMenuIsOpenContextProvider>
					{navItemsContext.visibleMenuIds.map((menuId) => (
						<ConfigurableMenuItem key={menuId} menuData={data} menuId={menuId} />
					))}
					{globalShortcuts}
					<TopLevelSpacer />
					<SidebarCustomization
						navItems={navItems}
						menuData={data}
						productItemsData={navItemsContext.productItemsData}
						appItemsData={appItemsDataStable}
						getAppShortcutURL={getAppShortcutURL}
					/>
				</MoreMenuIsOpenContextProvider>
			</PeekMenuIdContextProvider>
		</NavItemsContext.Provider>
	);
}
